import { RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { loadFillHeight, saveFillHeight } from "./state";

const FILL_MARGIN = 60;
// Single source of truth for the 55%-of-viewport baseline — also consumed by
// Grid.tsx (as the CSS `vh` fallback shown before the first measurement
// lands).
export const BASELINE_VIEWPORT_RATIO = 0.55;
const APP_WRAPPER_SELECTOR = ".monitor-dashboard-react-app";
// HHMCOVID-6327 — PatientDetailsPanel's always-rendered outer container.
// Measuring up to its top edge (not the whole app wrapper's bottom edge)
// keeps the baseline blind to whatever the panel currently shows — empty,
// its loading placeholder, or real content. Only Grid.tsx's fixed
// DETAILS_FOCUS_SHRINK accounts for the panel, uniformly either way.
const PANEL_SELECTOR = "#patient-details";
// How long the app wrapper's size must stay unchanged before it's considered
// "settled" and safe to measure. See the stabilization note below.
const STABILIZATION_DELAY_MS = 250;
// AC6 — debounce for live re-measurement on window resize/move-to-monitor.
const RESIZE_DEBOUNCE_MS = 250;

export interface FillAvailableHeight {
  fillHeight: number | null;
}

// Computes the grid height that lands the Patient Details panel's top edge
// `FILL_MARGIN` above the viewport bottom. Expressed in terms of bDiv's own
// current height (not the space above/below it), so it's self-consistent
// and safe to re-run at any time (AC6's live remeasurement).
function measureFillHeight(bDiv: Element, panelBoundary: Element): number {
  const currentHeight = bDiv.getBoundingClientRect().height;
  const boundaryTop = panelBoundary.getBoundingClientRect().top;
  return Math.round(currentHeight + window.innerHeight - FILL_MARGIN - boundaryTop);
}

function measureAndSave(bDiv: Element, appWrapper: Element, stateKey: string): FillAvailableHeight {
  const panelBoundary = appWrapper.querySelector(PANEL_SELECTOR) || appWrapper;
  const fillHeight = measureFillHeight(bDiv, panelBoundary);
  const result = { fillHeight };
  saveFillHeight(stateKey, result);
  return result;
}

/**
 * HHMCOVID-6327 AC2/AC5/AC6 — an explicit pixel height (not a `max-height`
 * ceiling, which never forces short content to grow) so the grid fills the
 * viewport. AC2 persists it per `stateKey` (AC1's manual resize always
 * wins); AC6 also keeps it live on window/monitor resize, debounced.
 *
 * Measures a focus-blind baseline only (see PANEL_SELECTOR above) — focus
 * handling is entirely Grid.tsx's job. Runs inside the dashboard's
 * `<iframe>`, so `window`/`document` here are the iframe's, not the
 * top-level page's.
 */
export function useFillAvailableHeight(
  bDivRef: RefObject<HTMLDivElement | null>,
  ready: boolean,
  hasManualOverride: boolean,
  stateKey: string
): FillAvailableHeight {
  const [result, setResult] = useState<FillAvailableHeight>(() => {
    const persisted = loadFillHeight(stateKey);
    return persisted ? { fillHeight: persisted.fillHeight } : { fillHeight: null };
  });
  // HHMCOVID-6327 — always starts false, even with a persisted value: a
  // persisted height predates this mount and can't reflect a monitor/zoom
  // change that happened while the page was closed. The layout effect below
  // always re-measures live; the persisted value is just the first-paint
  // placeholder until then.
  const hasMeasuredRef = useRef(false);
  // HHMCOVID-6327 — kept current via the effect below so the layout effect's
  // `measure()` (set up once per mount, not per `hasManualOverride` change)
  // always sees the latest value instead of the one captured at setup time.
  const hasManualOverrideRef = useRef(hasManualOverride);

  useEffect(() => {
    hasManualOverrideRef.current = hasManualOverride;
  }, [hasManualOverride]);

  useLayoutEffect(() => {
    if (!ready || hasMeasuredRef.current) {
      return;
    }

    const bDiv = bDivRef.current;
    if (!bDiv || typeof window === "undefined" || typeof ResizeObserver === "undefined") {
      return;
    }

    const appWrapper = bDiv.closest(APP_WRAPPER_SELECTOR);
    if (!appWrapper) {
      return;
    }

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const measure = () => {
      if (hasMeasuredRef.current) {
        return;
      }
      if (hasManualOverrideRef.current) {
        // A manual drag started during the stabilization debounce — skip
        // saving a manual-drag-influenced value as the auto-fit baseline.
        // Leave the observer connected (don't mark measured) so a later,
        // genuine auto-fit-mode resize can still complete the measurement.
        return;
      }
      hasMeasuredRef.current = true;
      observer.disconnect();
      setResult(measureAndSave(bDiv, appWrapper, stateKey));
    };

    // ResizeObserver always invokes its callback once on observe(), then again on every
    // subsequent size change — each firing (re)starts the debounce, so `measure` only
    // actually runs once the wrapper's size has held steady for STABILIZATION_DELAY_MS.
    const observer = new ResizeObserver(() => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(measure, STABILIZATION_DELAY_MS);
    });
    observer.observe(appWrapper);

    return () => {
      observer.disconnect();
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [ready, bDivRef, stateKey]);

  // AC6 — once the first measurement has landed, keep it current as the
  // browser window is resized or moved to a different monitor. Skipped while
  // a manual resize (AC1) is active: bDiv's rendered height is the manual
  // override in that case, not the auto-fit height this hook owns, so
  // measuring it would persist a bogus fill height under the AC2 key.
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const remeasure = () => {
      const bDiv = bDivRef.current;
      if (!hasMeasuredRef.current || hasManualOverride || !bDiv) {
        return;
      }
      const appWrapper = bDiv.closest(APP_WRAPPER_SELECTOR);
      if (!appWrapper) {
        return;
      }
      setResult(measureAndSave(bDiv, appWrapper, stateKey));
    };

    const onWindowResize = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(remeasure, RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [bDivRef, hasManualOverride, stateKey]);

  return result;
}
